import moment from "moment";

import { ConcurrencyTarget } from "../../models/concurrencyTargetEnum";
import { deleteEndpoint, get, post } from "../../utils/net";

import type { EncoderUserEntity } from "../../models";
import type { IdDescriptionBase } from "../../models/patientEncounter";
import type { InterfaceRegistration } from "../../scenes/SystemPreferences/models/interfaceRegistration";
import type {
  ActiveDomainSettings,
  AuditItemObject,
  DomainSettings,
  GeneralPreferenceObject,
  IPRange,
  IPRangeValidationResults,
  IPRestrictionObject,
  OrganizationObject,
  OrganizationSecurityOptionsObject,
  RemoteServicesSettingsObject,
  ReportsSettingsObject,
  SystemPreferencesObject,
  SystemPreferencesTypeSpecificObject,
  TestOrganizationSyncStatus,
} from "../../scenes/SystemPreferences/models/systemPreferences";

const baseURL = `${process.env.REACT_APP_API_PATH}/api`;

const getInterfaceTypes = (): Promise<IdDescriptionBase[]> => {
  const url = `${baseURL}/Interface/Types`;

  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result) => {
    const types = result.body.map((rec: { ViewId?: string; Name: string }) => ({
      id: rec.ViewId || "",
      title: rec.Name,
    }));
    return types;
  });
};

const getInterfaceSystemPreferences = (): Promise<SystemPreferencesObject> => {
  const url = `${baseURL}/SystemPreferences/Interface`;

  const extraOptions = {
    useExactPath: true,
    concurrencyTarget: ConcurrencyTarget.Encounter,
  };

  return get(url, extraOptions).then((result) => result.body);
};

const getInterfaceTypeSpecificPreferences = (
  type: string,
): Promise<SystemPreferencesTypeSpecificObject> => {
  const url = `${baseURL}/SystemPreferences/Interface/${type}`;

  const extraOptions = {
    useExactPath: true,
    concurrencyTarget: ConcurrencyTarget.Encounter,
  };

  return get(url, extraOptions).then((result) => result.body);
};

const updateInterfaceSystemPreferences = (
  _newPreferences: SystemPreferencesObject,
): Promise<SystemPreferencesObject> => {
  const url = `${baseURL}/SystemPreferences/Interface`;

  const newPreferences = _newPreferences;
  const Version = newPreferences.ConcurrencyVersion;
  const headers: { "If-Match"?: string } = {};

  if (Version) {
    headers["If-Match"] = Version;
    delete newPreferences.ConcurrencyVersion;
  }

  const extraOptions = {
    headers,
    useExactPath: true,
    concurrencyTarget: ConcurrencyTarget.Encounter,
  };

  return post(url, newPreferences, extraOptions).then((result) => result.body);
};

const updateInterfaceTypeSpecificPreferences = (
  type: string,
  typeSpecificPreferences: SystemPreferencesTypeSpecificObject,
): Promise<SystemPreferencesTypeSpecificObject> => {
  const url = `${baseURL}/SystemPreferences/Interface/${type}`;

  const newPreferences = typeSpecificPreferences;
  const Version = newPreferences.ConcurrencyVersion;
  const headers: { "If-Match"?: string } = {};

  if (Version) {
    headers["If-Match"] = Version;
    delete newPreferences.ConcurrencyVersion;
  }

  const extraOptions = {
    headers,
    useExactPath: true,
    concurrencyTarget: ConcurrencyTarget.Encounter,
  };

  return post(url, typeSpecificPreferences, extraOptions).then((result) => result.body);
};

const getRegisteredInterfaces = (): Promise<IdDescriptionBase[]> => {
  const url = `${baseURL}/SystemPreferences/InterfaceClientRegistrations`;

  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result) => result.body);
};

const registerInterface = (
  newRegistration: InterfaceRegistration,
): Promise<InterfaceRegistration> => {
  const url = `${baseURL}/SystemPreferences/InterfaceClientRegistrations`;

  const extraOptions = {
    useExactPath: true,
    concurrencyTarget: ConcurrencyTarget.Encounter,
  };

  return post(url, newRegistration, extraOptions).then((result) => result.body);
};

const deleteRegisteredInterface = (ViewId: string): Promise<boolean> => {
  const url = `${baseURL}/SystemPreferences/InterfaceClientRegistrations/${ViewId}`;

  const extraOptions = {
    useExactPath: true,
  };

  return deleteEndpoint(url, extraOptions).then((result) => result.body);
};

const getGeneralPreferences = (): Promise<GeneralPreferenceObject> => {
  const url = `${baseURL}/SystemPreferences/GetGeneralOptions`;

  const extraOptions = {
    useExactPath: true,
    concurrencyTarget: ConcurrencyTarget.Encounter,
  };

  return get(url, extraOptions).then((result) => result.body);
};

const updateGeneralPreferences = (
  option: GeneralPreferenceObject,
  ConcurrencyVersion: string,
): Promise<string> => {
  const url = `${baseURL}/SystemPreferences/UpdateGeneralOptions`;

  const version = ConcurrencyVersion || "";

  const headers = {
    "If-Match": version,
  };

  const extraOptions = {
    useExactPath: true,
    headers,
    concurrencyTarget: ConcurrencyTarget.Encounter,
  };

  return post(url, option, extraOptions).then((result) => result.body?.ConcurrencyVersion);
};

const getCurrentOrganization = (): Promise<OrganizationObject> => {
  const url = `${baseURL}/Organizations/Current`;

  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result) => ({
    ...result.body,
    ConcurrencyVersion: result.header.etag,
  }));
};

const setCurrentOrganization = (organization: OrganizationObject): Promise<OrganizationObject> => {
  const url = `${baseURL}/Organizations/Update`;

  const newOrganization = organization;
  const Version = organization.ConcurrencyVersion;
  const headers: { "If-Match"?: string } = {};

  if (Version) {
    headers["If-Match"] = Version;
    delete newOrganization.ConcurrencyVersion;
  }

  const extraOptions = {
    headers,
    useExactPath: true,
    concurrencyTarget: ConcurrencyTarget.Encounter,
  };

  return post(url, organization, extraOptions).then((result) => ({
    ...result.body,
    ConcurrencyVersion: result.header.etag,
  }));
};

const createTestOrganization = (options: OrganizationObject): Promise<OrganizationObject> => {
  const url = `${baseURL}/Organizations/Create/Test`;

  const extraOptions = {
    useExactPath: true,
  };

  return post(url, options, extraOptions).then((result) => result.body);
};

const syncTestOrganizationSettings = (options: OrganizationObject): Promise<unknown> => {
  const url = `${baseURL}/Organizations/SyncTestOrgSettings/Start`;

  const extraOptions = {
    useExactPath: true,
  };

  return post(url, options, extraOptions).then((result) => result);
};

const checkSyncTestOrganizationSettings = (): Promise<TestOrganizationSyncStatus> => {
  const url = `${baseURL}/Organizations/SyncTestOrgSettings/Status`;

  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result) => result.body);
};

const getOrganizationSecurityOptions = (): Promise<OrganizationSecurityOptionsObject> => {
  const url = `${baseURL}/Organizations/OrganizationSecurityOptions`;

  const extraOptions = {
    useExactPath: true,
    concurrencyTarget: ConcurrencyTarget.Encounter,
  };

  return get(url, extraOptions).then((result) => result.body);
};

const updateOrganizationSecurityOptions = (
  options: OrganizationSecurityOptionsObject,
): Promise<string> => {
  const url = `${baseURL}/Organizations/OrganizationSecurityOptions`;
  const version = options.ConcurrencyVersion || "";
  const newOptions = {
    ...options,
    ConcurrencyVersion: undefined,
  };

  const headers = {
    "If-Match": version,
  };

  const extraOptions = {
    useExactPath: true,
    headers,
    concurrencyTarget: ConcurrencyTarget.Encounter,
  };

  return post(url, newOptions, extraOptions).then((result) => result.body?.ConcurrencyVersion);
};

const getRemoteServicesSettings = async (): Promise<RemoteServicesSettingsObject> => {
  const url = `${baseURL}/SystemPreferences/RemoteServicesSettings`;

  const extraOptions = {
    useExactPath: true,
    concurrencyTarget: ConcurrencyTarget.Encounter,
  };

  const result = await get(url, extraOptions);
  return result.body;
};

const updateRemoteServicesSettings = async (
  settings: RemoteServicesSettingsObject,
): Promise<string> => {
  const url = `${baseURL}/SystemPreferences/RemoteServicesSettings`;
  const version = settings.ConcurrencyVersion || "";
  const newSettings = {
    ...settings,
    ConcurrencyVersion: undefined,
  };

  const headers = {
    "If-Match": version,
  };

  const extraOptions = {
    useExactPath: true,
    headers,
    concurrencyTarget: ConcurrencyTarget.Encounter,
  };

  const result = await post(url, newSettings, extraOptions);
  return result.body?.ConcurrencyVersion;
};

const getReportsSettings = async (): Promise<ReportsSettingsObject> => {
  const url = `${baseURL}/SystemPreferences/ReportsSettings`;

  const extraOptions = {
    useExactPath: true,
    concurrencyTarget: ConcurrencyTarget.Encounter,
  };

  const result = await get(url, extraOptions);
  return result.body;
};

const updateReportsSettings = async (settings: ReportsSettingsObject): Promise<string> => {
  const url = `${baseURL}/SystemPreferences/ReportsSettings`;
  const version = settings.ConcurrencyVersion || "";
  const newSettings = {
    ...settings,
    ConcurrencyVersion: undefined,
  };

  const headers = {
    "If-Match": version,
  };

  const extraOptions = {
    useExactPath: true,
    headers,
    concurrencyTarget: ConcurrencyTarget.Encounter,
  };

  const result = await post(url, newSettings, extraOptions);
  return result.body?.ConcurrencyVersion;
};

const getIPRestriction = async (): Promise<IPRestrictionObject> => {
  const response = await get(
    `${process.env.REACT_APP_API_PATH}/api/Organizations/IpWhitelistingOptions`,
    {
      concurrencyTarget: ConcurrencyTarget.Encounter,
      useExactPath: true,
    },
  );

  return response.body;
};

export const validateRangeList = async (list: IPRange[]): Promise<IPRangeValidationResults> => {
  const url = `${process.env.REACT_APP_API_PATH}/api/Organizations/IpWhitelistingOptions/Validate`;

  const extraOptions = {
    concurrencyTarget: ConcurrencyTarget.Encounter,
    useExactPath: true,
  };

  const body = {
    IpRanges: list,
  };

  const response = await post(url, body, extraOptions);

  return response.body;
};

const updateIPRestriction = async (settings: IPRestrictionObject): Promise<string> => {
  const url = `${process.env.REACT_APP_API_PATH}/api/Organizations/IpWhitelistingOptions`;

  const newSettings = {
    ...settings,
    ConcurrencyVersion: undefined,
  };

  const extraOptions = {
    useExactPath: true,
    headers: {
      "If-Match": settings.ConcurrencyVersion || "",
    },
    concurrencyTarget: ConcurrencyTarget.Encounter,
  };

  const result = await post(url, newSettings, extraOptions);

  return result.body?.ConcurrencyVersion;
};

export const getDomainSettings = async (): Promise<DomainSettings> => {
  const response = await get(`${process.env.REACT_APP_API_PATH}/api/Organizations/DomainSettings`, {
    concurrencyTarget: ConcurrencyTarget.Encounter,
    useExactPath: true,
  });

  return response.body;
};

export const updateDomainSettings = async (
  domainSettings: DomainSettings,
  concurrencyVersion?: string,
): Promise<string> => {
  const response = await post(
    `${process.env.REACT_APP_API_PATH}/api/Organizations/DomainSettings`,
    domainSettings,
    {
      concurrencyTarget: ConcurrencyTarget.Encounter,
      headers: { "If-Match": concurrencyVersion ?? null },
      useExactPath: true,
    },
  );

  return response.body?.ConcurrencyVersion;
};

export const getActiveDomainSettings = async (): Promise<ActiveDomainSettings> => {
  const response = await get(
    `${process.env.REACT_APP_API_PATH}/api/Organizations/DomainSettings/Active`,
    {
      useExactPath: true,
    },
  );

  return response.body;
};

const getAuditCategoryList = (): Promise<AuditItemObject[]> => {
  const url = `${baseURL}/AuditLogMaintenance/EventTypes`;

  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result) => result.body);
};

const getAuditExportLogCsv = (
  startDate?: string,
  endDate?: string,
  selectedUser?: EncoderUserEntity,
  auditList?: AuditItemObject[],
  timezoneOffset?: number,
): Promise<unknown> => {
  const url = `${baseURL}/AuditLogMaintenance/Export${createExportQueryString(startDate, endDate, selectedUser, auditList, timezoneOffset)}`;

  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result) => result);
};

const createExportQueryString = (
  startDate?: string,
  endDate?: string,
  selectedUser?: EncoderUserEntity,
  auditList?: AuditItemObject[],
  timezoneOffset?: number,
): string => {
  if (!(startDate || endDate)) {
    throw new Error("Start and/or end date are missing");
  }

  const utcStart = convertDateStringToUTC(startDate);
  const utcEnd = convertDateStringToUTC(`${endDate} 23:59:59`);

  const start = moment(utcStart, "ddd, D MMM YYYY HH:mm:ss").format("YYYY-MM-DD HH:mm:ss");
  const end = moment(utcEnd, "ddd, D MMM YYYY HH:mm:ss").format("YYYY-MM-DD HH:mm:ss");
  let queryString = `?From.Timestamp=${start}&To.Timestamp=${end}`;

  if (selectedUser) {
    queryString += `&Match.UserId=${selectedUser.userId}`;
  }

  if (auditList && auditList.length > 0) {
    if (!auditList.every((item) => item.Selected)) {
      auditList.forEach((item) => {
        if (item.Selected) {
          queryString += `&EventType=${item.Id}`;
        }
      });
    }
  }

  if (timezoneOffset !== undefined) {
    queryString += `&TimezoneOffset=${timezoneOffset}`;
  }

  // sort by datetime
  queryString += "&SortAsc.Timestamp=1";

  return queryString;
};

const convertDateStringToUTC = (date?: string) => {
  if (!date) {
    return undefined;
  }

  const dateValue = new Date(date);
  return dateValue.toUTCString();
};

const exportInterfaceLogs = async (includePHI: boolean): Promise<unknown> => {
  const url = `${baseURL}/interface/interfaceLogs/export?includePHI=${includePHI}`;

  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result) => result);
};

export const systemPreferencesAPI = {
  getInterfaceTypes,
  getInterfaceSystemPreferences,
  getInterfaceTypeSpecificPreferences,
  updateInterfaceSystemPreferences,
  updateInterfaceTypeSpecificPreferences,
  getRegisteredInterfaces,
  registerInterface,
  deleteRegisteredInterface,
  getGeneralPreferences,
  updateGeneralPreferences,
  getCurrentOrganization,
  setCurrentOrganization,
  createTestOrganization,
  syncTestOrganizationSettings,
  checkSyncTestOrganizationSettings,
  getOrganizationSecurityOptions,
  updateOrganizationSecurityOptions,
  getRemoteServicesSettings,
  updateRemoteServicesSettings,
  getAuditCategoryList,
  getAuditExportLogCsv,
  updateReportsSettings,
  getReportsSettings,
  getIPRestriction,
  updateIPRestriction,
  exportInterfaceLogs,
};
